import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Trash2, RefreshCw, Mail, Phone, Building2, Calendar, Inbox, CheckCheck, Eye,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

const STATUS_STYLES = {
  nouveau: { bg: "#00C8FF", color: "#0D2B45" },
  lu: { bg: "#CBD5E1", color: "#0D2B45" },
  "traité": { bg: "#10B981", color: "#FFFFFF" },
};

export default function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [devis, setDevis] = useState([]);
  const [stats, setStats] = useState({ total: 0, nouveau: 0, lu: 0, "traité": 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tous");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        api.get("/admin/devis"),
        api.get("/admin/stats"),
      ]);
      setDevis(d.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && (user === false || (user && user.role !== "admin"))) {
      nav("/admin/login", { replace: true });
    } else if (user && user.role === "admin") {
      fetchAll();
    }
  }, [user, authLoading, nav, fetchAll]);

  const updateStatus = async (id, newStatus) => {
    await api.patch(`/admin/devis/${id}`, null, { params: { new_status: newStatus } });
    fetchAll();
  };

  const removeDevis = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    await api.delete(`/admin/devis/${id}`);
    fetchAll();
  };

  const filtered = devis.filter((d) => filter === "tous" || d.status === filter);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F5F7FA" }}>
        <div style={{ color: "#475569" }}>Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#F5F7FA" }} data-testid="admin-dashboard-page">
      <header className="bg-ink sticky top-0 z-40" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="nx-container flex items-center justify-between" style={{ height: 88 }}>
          <Logo variant="full" height={44} />
          <div className="flex items-center gap-4">
            <span className="hidden md:block text-mist" style={{ fontSize: 15 }}>{user.email}</span>
            <button
              onClick={fetchAll}
              className="w-10 h-10 rounded-[10px] border border-white/20 flex items-center justify-center text-white hover:border-cyan hover:text-cyan transition-all"
              title="Rafraîchir"
              data-testid="admin-refresh"
            >
              <RefreshCw size={18} strokeWidth={1.75} />
            </button>
            <button
              onClick={() => { logout(); nav("/"); }}
              className="nx-btn nx-btn-outline"
              style={{ height: 44, padding: "0 20px", fontSize: 15 }}
              data-testid="admin-logout"
            >
              <LogOut size={16} strokeWidth={1.75} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="nx-container" style={{ paddingTop: 48, paddingBottom: 80 }}>
        <div style={{ marginBottom: 48 }}>
          <span className="nx-overline">Tableau de bord</span>
          <h1 style={{ marginTop: 16, marginBottom: 0, fontSize: 48 }}>Demandes de devis</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: 30, marginBottom: 40 }}>
          {[
            { label: "Total", value: stats.total, Icon: Inbox },
            { label: "Nouveaux", value: stats.nouveau, Icon: Mail },
            { label: "Lus", value: stats.lu, Icon: Eye },
            { label: "Traités", value: stats["traité"], Icon: CheckCheck },
          ].map(({ label, value, Icon }) => (
            <div key={label} className="nx-card" data-testid={`stat-${label.toLowerCase()}`}>
              <div className="nx-icon-wrap"><Icon size={22} strokeWidth={1.75} /></div>
              <div className="nx-overline" style={{ marginTop: 20 }}>{label}</div>
              <div style={{ fontSize: 42, fontWeight: 700, color: "#0D2B45", lineHeight: 1, marginTop: 8 }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap" style={{ marginBottom: 24 }}>
          {["tous", "nouveau", "lu", "traité"].map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="transition-all"
                style={{
                  padding: "10px 20px",
                  borderRadius: 10,
                  fontFamily: "Exo 2",
                  fontSize: 15,
                  fontWeight: 600,
                  textTransform: "capitalize",
                  border: `1.5px solid ${active ? "#0D2B45" : "#CBD5E1"}`,
                  background: active ? "#0D2B45" : "#FFFFFF",
                  color: active ? "#FFFFFF" : "#0D2B45",
                }}
                data-testid={`filter-${f}`}
              >
                {f}
              </button>
            );
          })}
        </div>

        {/* Devis List */}
        <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
          {loading ? (
            <div className="text-center" style={{ padding: 40, color: "#475569" }}>Chargement...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center" style={{ padding: 64 }} data-testid="admin-empty">
              <Inbox size={44} strokeWidth={1.5} className="mx-auto" style={{ color: "#CBD5E1", marginBottom: 16 }} />
              <div style={{ color: "#475569" }}>Aucune demande à afficher.</div>
            </div>
          ) : (
            <ul style={{ borderTop: "0" }}>
              {filtered.map((d, idx) => {
                const st = STATUS_STYLES[d.status] || { bg: "#CBD5E1", color: "#0D2B45" };
                return (
                  <li
                    key={d.id}
                    data-testid={`devis-item-${d.id}`}
                    style={{
                      padding: 32,
                      borderTop: idx === 0 ? "0" : "1px solid rgba(13,43,69,0.08)",
                    }}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-6">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span
                            style={{
                              padding: "4px 12px",
                              borderRadius: 6,
                              fontSize: 11,
                              letterSpacing: "0.14em",
                              textTransform: "uppercase",
                              fontWeight: 700,
                              background: st.bg,
                              color: st.color,
                            }}
                          >
                            {d.status}
                          </span>
                          <h4 style={{ margin: 0, fontSize: 22 }}>{d.nom}</h4>
                          <span className="flex items-center gap-1" style={{ fontSize: 13, color: "#475569" }}>
                            <Calendar size={13} strokeWidth={1.75} /> {formatDate(d.created_at)}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 8, marginTop: 16, fontSize: 15 }}>
                          <a href={`mailto:${d.email}`} className="flex items-center gap-2 hover:text-cyan" style={{ color: "#475569" }}>
                            <Mail size={14} strokeWidth={1.75} /> {d.email}
                          </a>
                          <a href={`tel:${d.telephone}`} className="flex items-center gap-2 hover:text-cyan" style={{ color: "#475569" }}>
                            <Phone size={14} strokeWidth={1.75} /> {d.telephone}
                          </a>
                          {d.societe && (
                            <span className="flex items-center gap-2" style={{ color: "#475569" }}>
                              <Building2 size={14} strokeWidth={1.75} /> {d.societe}
                            </span>
                          )}
                        </div>
                        <div className="nx-overline" style={{ marginTop: 16 }}>Service · {d.service}</div>
                        <p style={{ marginTop: 12, marginBottom: 0, fontSize: 16, whiteSpace: "pre-wrap" }}>{d.message}</p>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        {d.status !== "lu" && d.status !== "traité" && (
                          <button
                            onClick={() => updateStatus(d.id, "lu")}
                            style={{
                              padding: "10px 16px",
                              borderRadius: 10,
                              border: "1.5px solid #CBD5E1",
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#0D2B45",
                              background: "#FFFFFF",
                            }}
                            data-testid={`devis-mark-lu-${d.id}`}
                          >
                            Marquer lu
                          </button>
                        )}
                        {d.status !== "traité" && (
                          <button
                            onClick={() => updateStatus(d.id, "traité")}
                            style={{
                              padding: "10px 16px",
                              borderRadius: 10,
                              border: "1.5px solid #10B981",
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#FFFFFF",
                              background: "#10B981",
                            }}
                            data-testid={`devis-mark-traite-${d.id}`}
                          >
                            Marquer traité
                          </button>
                        )}
                        <button
                          onClick={() => removeDevis(d.id)}
                          style={{
                            padding: "10px 16px",
                            borderRadius: 10,
                            border: "1.5px solid #fecaca",
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#dc2626",
                            background: "#FFFFFF",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                          data-testid={`devis-delete-${d.id}`}
                        >
                          <Trash2 size={14} strokeWidth={1.75} /> Supprimer
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
